import {
  ActionIcon,
  Flex,
  Input,
  type InputProps,
  Text,
  Textarea,
  Tooltip,
} from "@mantine/core";
import { IconEdit, IconX } from "@tabler/icons-react";
import { type ReactNode, useEffect, useMemo, useRef } from "react";
import { hasFeature } from "src/generator/hooks/useCopyVal";
import { YESTERDAY_FEATURE } from "src/generator/utils/calcFeatures";
import { useTheme } from "src/hooks";

import useSavedCursorPosition from "../../hooks/useSavedCursorPosition";
import { useSelector } from "../../redux/hooks";
import CustomConverter from "../CustomConverter/CustomConverter";
import useAmbPrevData from "../hooks/useAmbPrevData";
import { useDynamicTextWidth } from "../hooks/useDynamicTextWidth";
import {
  getInputFieldName,
  requiredInputValidator,
  useInputProps,
} from "../hooks/useInputProps";
import useNMEAAutomated from "../hooks/useNMEAAutomated";
import useReadonlyClass from "../hooks/useReadonlyClass";
import { useTankValueValidator } from "../hooks/useTankValueValidator";
import { useLogbookRefsContext } from "../Logbook/LogbookRefsContext";
import { getClassName, getPreparedClassname } from "./CustomInput.utils";
import EntryTrendPopup from "./EntryTrendPopup";
import useAmbHeightCalc from "./useAmbHeightCalc";
import useAmbTotals from "./useAmbTotals";
import useAmbVoyageTotals from "./useAmbVoyageTotals";
import { useAmlCalcPerc, useAmlCalcTotal } from "./useAmlCalc";
import { useArbemaTotals } from "./useArbemaTotals";
import { useAddValues, useDifference } from "./useClassCalc";
import useDPFHoursCalculation from "./useDPFHoursCalculation";
import useEngineSelectData from "./useEngineSelectData";
import useFgasEqCalc from "./useFgasEqCalc";
import useFWCalculation from "./useFWCalculation";
import useManualEdit from "./useManualEdit";
import { useMoonFWDiff } from "./useMoonFWDiff";
import { useOnyxROBTotal } from "./useOnyxROBTotal";
import { useOnyxTankConvert } from "./useOnyxTankM3";
import useRoundDecimals from "./useRoundDecimals";
import { useSakuraTotalSum } from "./useSakuraPOBTotal";
import { useValorCalc } from "./useValorCalc";

interface ICustomInput extends InputProps {
  value: any;
  id: string;
  textarea?: boolean;
  required?: boolean;
  width?: string;
  suffix?: string | ReactNode;
  exclusive?: string;
  domId?: string;
  class?: string;
  feature?: string;
  decimals?: number | string;
  defaultValue?: string;
  rowIndex?: number;
  subrowIndex?: number;
  colIndex?: number;
  grayed?: boolean;
  prefix?: string;
  isOverlay?: boolean;
  readonly?: boolean;
  searchEntries?: string;
  tag?: string;
  // section context for vertical tabbing
  sectionIndex?: number;
  subSectionIndex?: number;
  // ambassador specific properties
  ambGetPreviousData?: string;
  ambHeightCalc?: string;
  ambVoyageTotals?: string;
  ambSteamingTotals?: string;
  ambSpeedTotals?: string;

  additionalOnChange?: (value: any, id?: string) => void;
  additionalOnBlur?: (e: any) => void;

  name?: string;
  source?: "auto" | "manual";
  originalValue?: number;

  amlGroup?: string;
  amlTotal?: string;
  amlCalc?: string;

  previousTotal?: number;
  manualEdit?: boolean;
  overwrite?: boolean;
  prefillValue?: any;

  convMT?: boolean;
  textWidth?: string;

  // numeric-mode specific
  numeric?: boolean;
  autoMinus?: boolean;
  sectionTag?: string;
  rowCols?: Record<string, any>;
  withTrend?: boolean;

  tankRef?: string;
  tankChecks?: Array<"limits" | "capacity">;
}

const CustomInput = ({
  value,
  id,
  textarea,
  required,
  width,
  suffix,
  exclusive,
  domId,
  class: className,
  feature,
  decimals,
  defaultValue,
  rowIndex,
  subrowIndex,
  colIndex,
  grayed,
  prefix,
  readonly,
  isOverlay,
  searchEntries,
  sectionIndex,
  subSectionIndex,
  ambGetPreviousData,
  ambHeightCalc,
  ambVoyageTotals,
  ambSpeedTotals,
  ambSteamingTotals,
  tag,
  additionalOnChange,
  additionalOnBlur,
  name,
  source,
  originalValue,
  amlGroup,
  amlTotal,
  amlCalc,
  previousTotal,
  convMT,
  textWidth,
  manualEdit,
  overwrite,
  prefillValue,
  numeric,
  autoMinus,
  sectionTag,
  rowCols,
  withTrend,
  tankRef,
  tankChecks,
  ...props
}: ICustomInput) => {
  const { features } = useSelector(state => state.generatorSlice);
  const { getEngineSelectValue } = useEngineSelectData();
  const theme = useTheme() as any;
  const readOnlyClass = useReadonlyClass({
    readonly: readonly ?? props.disabled,
    grayed,
  });
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const {
    enabled: hasTankRef,
    result: tankCheckResult,
    customValidator: tankCustomValidator,
  } = useTankValueValidator({
    tankRef,
    tankChecks,
    rowCols,
    fieldName: getInputFieldName(id, exclusive, undefined),
  });
  const combinedValidator = useMemo(() => {
    if (!hasTankRef) return undefined;
    return (v: any) =>
      requiredInputValidator(v, required) && tankCustomValidator(v);
  }, [hasTankRef, required, tankCustomValidator]);
  const {
    onChange: onInputChange,
    onBlur: onInputBlur,
    setValue,
    ...inputProps
  } = useInputProps(
    id,
    value ?? defaultValue,
    required,
    exclusive,
    combinedValidator,
  );

  // Calc hooks below write through calcSetValue so a manual value is kept
  const canManuallyEdit =
    Boolean(overwrite) ||
    Boolean(ambVoyageTotals ?? ambSpeedTotals ?? ambSteamingTotals);
  const { calcSetValue, setManualEdit, recalcKey } = useManualEdit({
    id,
    enabled: canManuallyEdit,
    manualEdit,
    initialValue: value ?? defaultValue,
    prefillValue,
    sectionIndex,
    rowIndex,
    setValue,
  });

  // onyx tank specific hooks - adds classes and handles conversion
  useOnyxTankConvert({ id, tag, tankGroup: `tank-${rowIndex?.toString()}` });

  // onyx ROB specific hooks - calculates percentage from ROB value and adds classes
  useOnyxROBTotal({ id, tag, robGroup: `rob-${rowIndex?.toString()}` });

  // sakura total hook - only for TOTAL field
  useSakuraTotalSum({ tag: domId || className, id, setValue: calcSetValue });

  // moonrise fw diff hook
  useMoonFWDiff(tag ?? "", previousTotal, id, calcSetValue);

  // ambassador logbook specific hooks for tooling
  useAmbHeightCalc({ ambHeightCalc, id, setValue });
  useAmbPrevData({ id, dataTag: ambGetPreviousData, value: inputProps.value });
  useAmbTotals({
    id,
    setValue,
    manualEdit: manualEdit ?? false,
    ambVoyageTotals,
    ambSteamingTotals,
    ambSpeedTotals,
  });

  useAmbVoyageTotals({
    id,
    setValue,
    rowIndex: rowIndex ?? 0,
    tag,
  });

  useArbemaTotals({
    id,
    tag,
    defaultValue: originalValue,
  });

  useAmlCalcTotal({
    id,
    setValue: calcSetValue,
    amlGroup,
    amlTotal,
  });

  useAmlCalcPerc({
    id,
    setValue: calcSetValue,
    amlCalc,
    feature,
    rowIndex: rowIndex ?? 0,
  });

  // DPF hours calculation hook
  useDPFHoursCalculation({ id, tag, setValue: calcSetValue });

  // Fresh Water calculation hook
  useFWCalculation({ id, tag, setValue: calcSetValue });

  // Valor Total Fuel calculation hook
  useValorCalc({ sectionTag, tag, rowIndex, id, setValue: calcSetValue });

  useFgasEqCalc({ id, feature, cols: rowCols });

  const classCalcProps = {
    id,
    feature,
    className,
    setValue: calcSetValue,
    recalcKey,
  };
  useAddValues(classCalcProps);
  useDifference(classCalcProps);

  // Kept last so it rounds whatever the hooks above wrote
  const rounding = useRoundDecimals({
    id,
    value: inputProps.value,
    decimals,
    enabled: numeric && !isOverlay,
    setValue,
  });

  // useAmbTotalLog({
  //   defaultValue: originalValue ?? parseFloat(defaultValue ?? "") ?? 0,
  //   id,
  //   setValue,
  //   className: ambVoyageTotals === "totalLog" ? "totalLog" : className,
  //   rowIndex,
  //   isOverlay,
  //   inputRef,
  // });

  const { handleNMEAChange } = useNMEAAutomated({
    id,
    source,
    sectionIndex,
    defaultValue,
    name,
  });

  const {
    handleFocus,
    handleBlur: handleCursorBlur,
    setCursorPosition,
  } = useSavedCursorPosition(id, inputRef);

  // Get logbook refs context props for navigation
  const logbookRefsProps = useLogbookRefsContext(id);

  useEffect(() => {
    if (isOverlay) return;
    const valueId = feature === "autoday" ? "autoday" : domId;
    if (feature === "contactNumber" && features.contactNumber !== undefined) {
      setValue(id, features.contactNumber);
      return;
    }
    if (valueId && features[valueId] !== undefined)
      setValue(id, features[valueId]);

    const popTags = ["full_name_caps"];
    if (tag && popTags.includes(tag) && features[tag] !== undefined) {
      setValue(id, features[tag]);
    }
  }, [domId, features, id, isOverlay, feature, setValue, tag]);

  const minInputWidth = useMemo(() => {
    if (!width || width.includes("%")) return 200;
    return Number(width) * 10;
  }, [width]);

  const {
    dynamicWidth: dynamicInputWidth,
    sizerRef,
    updateDynamicWidth,
  } = useDynamicTextWidth({
    enabled: textWidth === "dynamic",
    value: inputProps.value,
    minWidth: minInputWidth,
    inputRef,
  });

  const prevValue = useRef((value ?? defaultValue)?.toString());

  const handleNumericChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = event.target.value;
    if (inputValue === "" || /^[+-]?\d*(\.\d*)?$/.test(inputValue)) {
      if (
        autoMinus &&
        prevValue.current?.toString() === "" &&
        ![".", ",", "-"].includes(inputValue)
      ) {
        inputValue = `-${inputValue}`;
      }
      event.target.value = inputValue;
      prevValue.current = inputValue;
      setCursorPosition();
      additionalOnChange?.(inputValue, id);
      rounding.markTyped();
      onInputChange(inputValue);
      if (overwrite) setManualEdit(true);
    }
  };

  // shouldHighlightValue is currently a no-op stub, kept as-is from CustomNumberInput
  const shouldHighlightValue = (
    highlightTag: string | undefined,
    highlightValue: any,
  ): boolean => {
    // if (highlightTag === "valorC" && parseFloat(highlightValue) < 0) return true;
    return false;
  };

  const handleInputChange = (e?: any, withoutTimeout?: boolean) => {
    if (numeric) {
      if (e) handleNumericChange(e);
      return;
    }
    if (isOverlay) return;
    setCursorPosition();
    if (textWidth === "dynamic" && textarea && e?.target) {
      updateDynamicWidth(
        `${e.target.value ?? ""}`,
        e.target as HTMLTextAreaElement,
      );
    }
    if (e) {
      onInputChange(e);
      additionalOnChange?.(e);
      if (overwrite) setManualEdit(true);
    }
  };

  const isReadOnly =
    (ambVoyageTotals ?? ambSpeedTotals ?? ambSteamingTotals) &&
    !canManuallyEdit;

  const isPrefilled =
    hasFeature(feature, YESTERDAY_FEATURE) ||
    Boolean(domId?.endsWith("_total_yesterday"));
  const manualLabel = isPrefilled
    ? "Restore prefilled value"
    : "Remove manual value";
  const autoLabel = isPrefilled ? "Prefilled value" : "Calculated value";

  const manualEditIcon = canManuallyEdit ? (
    <Tooltip label={manualEdit ? manualLabel : autoLabel} withArrow>
      <ActionIcon
        size="sm"
        color={manualEdit ? "red" : "green"}
        onClick={() => manualEdit && setManualEdit(false)}>
        {manualEdit ? <IconX size={16} /> : <IconEdit size={16} />}
      </ActionIcon>
    </Tooltip>
  ) : null;

  const handleBlur = (e: any) => {
    if (numeric) {
      rounding.roundOnBlur();
      handleNMEAChange(inputProps.value);
      onInputBlur();
      handleCursorBlur(e);
      additionalOnBlur?.(e);
      return;
    }
    if (isOverlay) return;
    onInputBlur();
    handleNMEAChange(inputProps.value);
    handleCursorBlur(e);
    additionalOnBlur?.(e);
    if (!overwrite) setManualEdit(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Call logbook refs context keyDown handler first for navigation
    logbookRefsProps.onKeyDown(e);

    // Restrict input to numbers for tank and ROB fields
    const isOnyxField =
      tag === "onyxTankValue" ||
      tag === "onyxTankM3" ||
      (tag && tag.startsWith("OnyxROBTotal"));

    if (isOnyxField) {
      // Allow: backspace, delete, tab, escape, enter, period, minus
      const allowedKeys = [
        "Backspace",
        "Delete",
        "Tab",
        "Escape",
        "Enter",
        ".",
        "-",
      ];
      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
      const allowedCtrlKeys = ["a", "c", "v", "x", "z"];

      if (
        allowedKeys.includes(e.key) ||
        (e.ctrlKey && allowedCtrlKeys.includes(e.key.toLowerCase())) ||
        // Allow numbers (both regular and numpad)
        (!isNaN(Number(e.key)) && e.key !== " ")
      ) {
        return;
      }

      // Prevent default for all other keys
      e.preventDefault();
    }
  };

  const disabled = useMemo(
    () =>
      readonly ??
      props.disabled ??
      inputProps.disabled ??
      isOverlay ??
      Boolean(
        className && features[`${className}`] && className?.includes("grey"),
      ) ??
      // Disable OnyxPercTotal fields - they should only be calculated, not manually edited
      (tag && tag.startsWith("OnyxPercTotal")),
    [props.disabled, className, features, inputProps, isOverlay, readonly, tag],
  );

  const parsedClassName = getPreparedClassname(
    ambHeightCalc,
    tag,
    ambVoyageTotals,
    amlGroup,
    amlCalc,
    className,
    domId,
    colIndex,
    rowIndex ?? 0,
    getEngineSelectValue,
  );

  if (textarea) {
    return (
      <div
        style={{
          position: "relative",
          width: textWidth === "dynamic" ? `${dynamicInputWidth}px` : "100%",
        }}>
        {textWidth === "dynamic" && (
          <span
            ref={sizerRef}
            aria-hidden
            style={{
              position: "absolute",
              visibility: "hidden",
              whiteSpace: "pre",
              pointerEvents: "none",
            }}
          />
        )}
        <Textarea
          className={` ${getClassName(parsedClassName, rowIndex)}${isOverlay ? `_overlay ${readOnlyClass}` : ` ${readOnlyClass}`}`}
          autosize
          minRows={1}
          miw={textWidth === "dynamic" ? dynamicInputWidth : 25}
          wrap={textWidth === "dynamic" ? "off" : "soft"}
          {...props}
          {...inputProps}
          ref={el => {
            inputProps.ref(el);
            inputRef.current = el;
            logbookRefsProps.ref(el);
          }}
          styles={{
            input: {
              paddingTop: "6px !important",
              paddingBottom: "6px !important",
              whiteSpace: textWidth === "dynamic" ? "pre" : undefined,
              ...inputProps.styles.input,
            },
          }}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleInputChange}
        />
      </div>
    );
  }
  const trendProps = {
    value: inputProps.value,
    name,
    suffix: typeof suffix === "string" ? suffix : undefined,
    sectionIndex,
    subSectionIndex,
    rowIndex,
    subRowIndex: subrowIndex,
    colIndex,
    disabled: isOverlay,
  };
  const tankBorderStyles =
    hasTankRef && tankCheckResult.level === "warn"
      ? { input: { borderColor: "var(--mantine-color-orange-5)" } }
      : hasTankRef && tankCheckResult.level === "error"
        ? { input: { borderColor: "var(--mantine-color-red-6)" } }
        : undefined;
  const tankInputError =
    Boolean(inputProps.error) ||
    (hasTankRef && tankCheckResult.level === "error");

  const wrapTankTooltip = (node: ReactNode) => {
    if (!hasTankRef) return node;
    // Always render Tooltip so the input isn't remounted (and blurred) when the message toggles.
    return (
      <Tooltip
        withArrow
        disabled={!tankCheckResult.message}
        label={tankCheckResult.message}
        color={tankCheckResult.level === "error" ? "red" : "orange"}>
        <div style={{ width: "100%" }}>{node}</div>
      </Tooltip>
    );
  };

  if (numeric) {
    return wrapTankTooltip(
      <EntryTrendPopup {...trendProps}>
        <Input
          id={id}
          className={getClassName(tag, rowIndex)}
          miw={Number(width) * 10}
          {...props}
          {...inputProps}
          ref={el => {
            inputProps.ref(el);
            inputRef.current = el;
            logbookRefsProps.ref(el);
          }}
          error={tankInputError}
          styles={{
            input: {
              color: shouldHighlightValue(tag, value)
                ? theme.colors.redText
                : "inherit",
              ...tankBorderStyles?.input,
            },
          }}
          onKeyDown={logbookRefsProps.onKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleInputChange}
          rightSection={
            canManuallyEdit ? (
              <Flex gap={4} align="center">
                {manualEditIcon}
                {suffix}
              </Flex>
            ) : (
              suffix
            )
          }
          rightSectionProps={{
            style: {
              width: "max-content",
              padding: "0 10px",
            },
          }}
        />
      </EntryTrendPopup>,
    );
  }

  // tag &&
  //   console.log({
  //     tag,
  //     props,
  //   });

  const showConverter = (false || convMT) && !disabled; // add additional conditions if needed
  const mtProps = {
    rows: [
      {
        fields: ["Y [m3]", "Z [mt/m3]"],
        divisors: [1, 1],
        result: "X [mt]",
      },
      {
        fields: ["Y [m3]", "Z [kg/m3]"],
        divisors: [1, 1000],
        result: "X [mt]",
      },
    ],
    type: "multiply" as const,
    fieldId: id,
  };
  // now supports percentages
  width = width
    ? width.includes("%")
      ? width
      : `${Number(width) * 10}px`
    : width;
  const parsedIdx: any = ambHeightCalc ?? rowIndex ?? 0;
  return wrapTankTooltip(
    <EntryTrendPopup {...trendProps} disabled={!withTrend || isOverlay}>
      <Input
        className={` ${getClassName(parsedClassName, parsedIdx)}${isOverlay ? `_overlay ${readOnlyClass}` : ` ${readOnlyClass}`}`}
        miw={width}
        rightSection={
          (showConverter ?? suffix ?? canManuallyEdit) ? (
            <Flex gap={4}>
              {showConverter && <CustomConverter {...mtProps} />}
              {manualEditIcon}
              {suffix}
            </Flex>
          ) : undefined
        }
        onChange={handleInputChange}
        rightSectionProps={
          (showConverter ?? suffix ?? canManuallyEdit)
            ? {
                style: {
                  display: "flex",
                  width: "max-content",
                  padding: "0 10px",
                  color: "var(--mantine-color-dimmed)",
                },
              }
            : undefined
        }
        {...props}
        {...inputProps}
        error={tankInputError}
        styles={{
          ...inputProps.styles,
          input: {
            ...inputProps.styles?.input,
            ...tankBorderStyles?.input,
          },
        }}
        placeholder={prefix ?? ""}
        ref={el => {
          inputProps.ref(el);
          inputRef.current = el;
          logbookRefsProps.ref(el);
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        readOnly={!!isReadOnly}
        {...(amlCalc && { "data-aml": amlCalc })}
      />
    </EntryTrendPopup>,
  );
};

export default CustomInput;
